import {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  ICellRendererParams,
  IRowDragItem,
  RowDragEndEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AgGridReact } from 'ag-grid-react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { ChangeEvent, FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { H2 } from '../../../../common/Headings/Headings.styles';
import { TableRowWrapper } from '../../../../common/SideBySideTableLayout/SideBySideTableLayout.styles';
import Button from '../../../../components/Button/Button';
import { Variant } from '../../../../components/Button/Button.types';
import { EditCellRenderer } from '../../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../../components/Table/Table';
import { showToast } from '../../../../components/Toast/Toast';
import { ToastTypes } from '../../../../components/Toast/Toast.types';
import { useGetRawProducts } from '../../../../services/rawProd/rawProd.service';
import { RawProd } from '../../../../services/rawProd/rawProd.types';
import { genericErrorShowToastOptions } from '../../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { Buttons, SelectRawProdsContainer, StyledTableWrapper, CloseButton } from './SelectRawProds.styles';
import { SelectRawProdsProps } from './SelectRawProds.types';
import DragAndDrop from '../../../../components/DragAndDrop/DragAndDrop';
import {
  CellTextWrapper,
  EditCell,
  IconButton,
} from '../../../../components/Table/EditCellRenderer/EditCellRenderer.styles';
import { useTheme } from '@emotion/react';
import RawProductSelectTable from "../../../../components/RawProduct/RawProductSelectTable";

const SelectRawProds: FC<SelectRawProdsProps> = ({
  selectedRawProds,
  leftRowData,
  centerRowData,
  rightRowData,
  setLeftRowData,
  setRightRowData,
  setCenterRowData,
  setSelectedRawProds,
  setStep,
  flow = 'addRawProds',
}) => {
  const [leftApi, setLeftApi] = useState<GridApi | null>(null);
  const [rightApi, setRightApi] = useState<GridApi | null>(null);
  const [centerApi, setCenterApi] = useState<GridApi | null>(null);
  const availableRawProductGridRef = useRef<AgGridReact>(null);
  const selectedGridRef = useRef<AgGridReact>(null);
  const rightRef = useRef<AgGridReact>(null);
  const centerRef = useRef<AgGridReact>(null);
  const leftRef = useRef<AgGridReact>(null);

  const { data: rawProdsData, isError: rawProdsError } = useGetRawProducts(true);

  const [rawProdNo, setRawProdNo] = useState('');
  const [rawProdNoError, setRawProdNoError] = useState(false);

  const allRawProds = rawProdsData?.data || [];
  const theme = useTheme();

  const EditDragAndDropCellRenderer = (props: ICellRendererParams) => {
    return (
      <EditCell>
        <CellTextWrapper>{props.value}</CellTextWrapper>
        <IconButton aria-label='edit'>
          <EditOutlinedIcon htmlColor={theme.colors.blue} />
        </IconButton>
        <></>
      </EditCell>
    );
  };

  const RemoveCellRenderer = (props: ICellRendererParams) => {
    return (
      <EditCell>
        <CloseButton>
          <CloseOutlinedIcon
            fontSize='inherit'
            color='inherit'
            style={{ cursor: 'pointer', height: '100%' }}
            onClick={() => {
              props.api.applyTransaction({ remove: [props.node.data] });
              onRemoveRow(props, 2);
            }}
          />
        </CloseButton>
      </EditCell>
    );
  };

  const RemoveRightCellRenderer = (props: ICellRendererParams) => {
    return (
      <EditCell>
        <CloseButton>
          <CloseOutlinedIcon
            fontSize='inherit'
            color='inherit'
            style={{ cursor: 'pointer', height: '100%' }}
            onClick={() => {
              props.api.applyTransaction({ remove: [props.node.data] });
              onRemoveRow(props, 3);
            }}
          />
        </CloseButton>
      </EditCell>
    );
  };

  const dragHandleRawProdText = (params: IRowDragItem) => {
    return params.rowNode?.data?.moaRawProdNo + ' - ' + params.rowNode?.data?.moaRawProdDesc;
  };

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      let foundRecord = false;
      availableRawProductGridRef?.current?.api?.forEachNode((node) => {
        if (node.data.moaRawProdNo.toString() === rawProdNo) {
          node.setSelected(true);
          foundRecord = true;
        }
      });
      if (!foundRecord) {
        setRawProdNoError(true);
      }
    }
  };

  const editableColumn: ColDef[] = [
    {
      minWidth: 70,
      maxWidth: 70,
      flex: 1,
      floatingFilter: false,
      filterParams: false,
      sortable: false,
      rowDrag: true,
      rowDragText: dragHandleRawProdText,
    },
    { minWidth: 100, maxWidth: 120, flex: 0.5, field: 'moaRawProdNo', headerName: 'Raw Product' },
    { minWidth: 180, flex: 1, field: 'moaRawProdDesc', headerName: 'Description', resizable: true },
    {
      minWidth: 100,
      maxWidth: 200,
      flex: 0.4,
      field: 'usageAmount',
      headerName: 'Usage Amount',
      editable: true,
      resizable: true,
      cellRenderer: EditDragAndDropCellRenderer,
      singleClickEdit: true,
    },
    {
      minWidth: 70,
      maxWidth: 70,
      flex: 1,
      field: 'remove',
      headerName: '',
      editable: true,
      resizable: false,
      cellRenderer: RemoveCellRenderer,
    },
  ];

  const editableRightColumn: ColDef[] = [
    {
      minWidth: 70,
      maxWidth: 70,
      flex: 1,
      floatingFilter: false,
      filterParams: false,
      sortable: false,
      rowDrag: true,
      rowDragText: dragHandleRawProdText,
    },
    { minWidth: 100, maxWidth: 180, flex: 0.5, field: 'moaRawProdNo', headerName: 'Raw Product' },
    { minWidth: 180, flex: 1, field: 'moaRawProdDesc', headerName: 'Description', resizable: true },
    {
      minWidth: 100,
      maxWidth: 200,
      flex: 0.4,
      field: 'usageAmount',
      headerName: 'Usage Amount',
      editable: true,
      resizable: true,
      cellRenderer: EditDragAndDropCellRenderer,
      singleClickEdit: true,
    },
    {
      minWidth: 70,
      maxWidth: 70,
      flex: 1,
      field: 'remove',
      headerName: '',
      editable: true,
      resizable: false,
      cellRenderer: RemoveRightCellRenderer,
    },
  ];

  useEffect(() => {
    if (rawProdsError)
      showToast({
        ...genericErrorShowToastOptions,
        label: 'Error fetching Raw Products.',
        id: 'rawProdFetchError',
        toastType: ToastTypes.Error,
      });
  }, [rawProdsError]);

  const selectAvailableRawProds = (selectedRows: RawProd[]) => {
    availableRawProductGridRef?.current?.api?.forEachNode((node) => {
      const matchingItem = selectedRows.find(
        (selectedRawProd) => selectedRawProd.moaRawProdNo === node?.data?.moaRawProdNo
      );
      if (matchingItem) {
        node.setSelected(true);
      } else {
        node.setSelected(false);
      }
    });
  };

  useEffect(() => {
    if (selectedRawProds) {
      selectedGridRef?.current?.api?.selectAll();
      selectAvailableRawProds(selectedRawProds);
    }
  });

  const loadGrids = useCallback(() => {
    if (allRawProds) {
      setLeftRowData([...allRawProds]);
      setRightRowData(rightRowData);
      setCenterRowData(centerRowData);
    }
  }, [allRawProds]);

  useEffect(() => {
    if (allRawProds?.length) {
      loadGrids();
    }
  }, [allRawProds, loadGrids]);

  const onDragCenterStop = useCallback(
    (params: RowDragEndEvent) => {
      const rowData = params.node.data;
      setCenterRowData((prev: any) => {
        const existsInSelected = prev.findIndex((rawProd: any) => rawProd.moaRawProdNo === rowData.moaRawProdNo);
        if (existsInSelected === -1) {
          return [
            ...prev,
            {
              moaRawProdNo: rowData.moaRawProdNo,
              moaRawProdDesc: rowData.moaRawProdDesc,
              usageAmount: null,
            },
          ];
        } else {
          return [...prev];
        }
      });
      return params.nodes;
    },
    [centerApi]
  );

  const onRemoveRow = (props: ICellRendererParams, side: number) => {
    const rowData = props.data;
    if (side === 2) {
      setCenterRowData((prev: any) => {
        const existsInSelected = prev.findIndex((rawProd: any) => rawProd.moaRawProdNo === rowData.moaRawProdNo);
        if (existsInSelected !== -1) {
          prev.splice(existsInSelected, 1);
        }
        return [...prev];
      });
    } else if (side === 3) {
      setRightRowData((prev: any) => {
        const existsInSelected = prev.findIndex((rawProd: any) => rawProd.moaRawProdNo === rowData.moaRawProdNo);
        if (existsInSelected !== -1) {
          prev.splice(existsInSelected, 1);
        }
        return [...prev];
      });
    }
  };

  const onDragRightStop = useCallback(
    (params: RowDragEndEvent) => {
      const rowData = params.node.data;
      setRightRowData((prev: any) => {
        const existsInSelected = prev.findIndex((rawProd: any) => rawProd.moaRawProdNo === rowData.moaRawProdNo);
        if (existsInSelected === -1) {
          return [
            ...prev,
            {
              moaRawProdNo: rowData.moaRawProdNo,
              moaRawProdDesc: rowData.moaRawProdDesc,
              usageAmount: null,
            },
          ];
        } else {
          return [...prev];
        }
      });
      return params.nodes;
    },
    [rightApi]
  );

  useEffect(() => {
    if (!leftApi || !rightApi || !centerApi) {
      return;
    }
    const rightDropZoneParams = rightApi.getRowDropZoneParams({ onDragStop: onDragRightStop });
    const rightDropzoneForCenter = rightApi.getRowDropZoneParams({ onDragStop: onDragRightStop });    const centerDropZoneParams = centerApi?.getRowDropZoneParams({ onDragStop: onDragCenterStop });
    const centerDropZoneForRight = centerApi?.getRowDropZoneParams({ onDragStop: onDragCenterStop });

    if (centerDropZoneParams) leftApi?.addRowDropZone(centerDropZoneParams);
    if (rightDropZoneParams) leftApi?.addRowDropZone(rightDropZoneParams);
    if (centerDropZoneForRight) rightApi?.addRowDropZone(centerDropZoneForRight);
    if (rightDropzoneForCenter) centerApi?.addRowDropZone(rightDropzoneForCenter);
  }, [leftApi, rightApi, onDragRightStop, onDragCenterStop, centerApi]);

  const onGridReady = (params: GridReadyEvent, side: number) => {
    if (side === 1) {
      setLeftApi(params.api);
    }
    if (side === 2) {
      setCenterApi(params.api);
    }
    if (side === 3) {
      setRightApi(params.api);
    }
  };
  const getRowId = (params: GetRowIdParams) => params.data.moaRawProdNo;

  const onSelectionChangedAvailable = (event: SelectionChangedEvent) => {
    setSelectedRawProds(
      event.api.getSelectedRows().map((row) => {
        const existsInSelected = selectedRawProds.find((rawProd) => rawProd.moaRawProdNo === row.moaRawProdNo);
        return {
          ...row,
          usageAmount:
            flow === 'addRawProds' ? existsInSelected?.usageAmount || 0 : existsInSelected?.usageAmount || '',
        };
      })
    );
  };

  const onSelectionChangedSelected = (event: SelectionChangedEvent) => {
    selectAvailableRawProds(event.api.getSelectedRows());
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRawProdNo(event.target.value);
    setRawProdNoError(false);
  };

  const handleAddRawProd = (event: MouseEvent) => {
    event.preventDefault();
    let foundRecord = false;
    availableRawProductGridRef?.current?.api?.forEachNode((node) => {
      if (node.data.moaRawProdNo.toString() === rawProdNo) {
        node.setSelected(true);
        foundRecord = true;
      }
    });
    if (!foundRecord) {
      setRawProdNoError(true);
    }
  };

  const commonHandleCellEdit = (event: any, rowData: any[]) => {
    let newValue: number | undefined = +event.newValue;
    if (newValue > 9999.9999) newValue = 9999.9999;
    if (newValue < -9999.9999) newValue = -9999.9999;
    if (Number.isNaN(newValue) || event.newValue.trim().length === 0) {
      if (event.newValue !== undefined && event.newValue.length === 0) {
        // NewValue enforced to undefined when empty string is received
        newValue = undefined;
      } else {
        return rowData;
      }
    }

    return rowData.map((rawProd) => {
      if (rawProd.moaRawProdNo === event.data.moaRawProdNo) {
        return { ...rawProd, usageAmount: newValue };
      } else {
        return rawProd;
      }
    });
  };
  const handleCellEdit = useCallback(
    (event: any) => {
      setSelectedRawProds(commonHandleCellEdit(event, selectedRawProds));
    },
    [selectedRawProds]
  );

  const handleCenterCellEdit = useCallback(
    (event: any) => {
      setCenterRowData(commonHandleCellEdit(event, centerRowData));
    },
    [centerRowData]
  );

  const handleRightCellEdit = useCallback(
    (event: any) => {
      setRightRowData(commonHandleCellEdit(event, rightRowData));
    },
    [rightRowData]
  );

  const stepInstructionsText = () => {
    if (flow === 'addRawProds') {
      return <H2>Select raw products to add and input usage amount</H2>;
    }
    if (flow === 'currentRawProduct') {
      return <H2>Select the current raw product to be changed</H2>;
    }
    if (flow === 'desiredRawProduct') {
      return <H2>Select the desired raw product to replace with and input usage amount</H2>;
    }
  };

  const selectedTableHeaderTitle = () => {
    if (flow === 'currentRawProduct') {
      return 'Selected Raw Product';
    }
    if (flow === 'desiredRawProduct') {
      return 'Desired Raw Product';
    }
    return 'Selected Raw Products';
  };

  const handleNextClick = () => {
    if (flow === 'addRawProds' || flow === 'currentRawProduct') setStep(1);
    if (flow === 'desiredRawProduct') setStep(2);
  };

  const onFirstDataRendered = () => {
    const inputElement = document.querySelector(
      'input.ag-input-field-input.ag-text-field-input'
    ) as HTMLInputElement;
    inputElement?.focus();
  }

  return (
    <SelectRawProdsContainer>
      {stepInstructionsText()}
      <TableRowWrapper>
        {flow === 'currentRawProduct' ? (
          <>
            <StyledTableWrapper>
              <DragAndDrop
                ref={leftRef}
                showHeader={true}
                headerTitle='Available Raw Products'
                onFirstDataRendered={onFirstDataRendered}
                columnDefs={[
                  {
                    minWidth: 70,
                    maxWidth: 70,
                    flex: 1,
                    floatingFilter: false,
                    sortable: false,
                    rowDrag: true,
                    rowDragText: dragHandleRawProdText,
                  },
                  { minWidth: 120, flex: 0.5, field: 'moaRawProdNo', headerName: 'Raw Product' },
                  { minWidth: 220, flex: 1, field: 'moaRawProdDesc', headerName: 'Description', resizable: true, filter: 'agTextColumnFilter', cellRenderer: 'agGroupCellRenderer' },
                ]}
                rowData={leftRowData}
                suppressRowClickSelection={true}
                suppressMoveWhenRowDragging={true}
                returnedRecords={leftRowData?.length || 0}
                hasFilter={true}
                getRowId={getRowId}
                height={'100%'}
                gridApi={leftApi}
                onGridReady={(params) => onGridReady(params, 1)}
              />
            </StyledTableWrapper>

            <StyledTableWrapper>
              <DragAndDrop
                showHeader={true}
                ref={centerRef}
                rowDragManaged={true}
                headerTitle='Current Raw Products'
                columnDefs={editableColumn}
                rowData={centerRowData}
                hasFilter={false}
                returnedRecords={centerRowData.length || 0}
                height={'100%'}
                gridApi={centerApi}
                getRowId={getRowId}
                onGridReady={(params) => onGridReady(params, 2)}
                onCellEditRequest={handleCenterCellEdit}
                readOnlyEdit={true}
              />
            </StyledTableWrapper>
            <StyledTableWrapper>
              <DragAndDrop
                showHeader={true}
                ref={rightRef}
                headerTitle='Desired Raw Products'
                columnDefs={editableRightColumn}
                rowDragManaged={true}
                hasFilter={false}
                rowData={rightRowData}
                getRowId={getRowId}
                returnedRecords={rightRowData.length || 0}
                height={'100%'}
                gridApi={rightApi}
                onGridReady={(params) => onGridReady(params, 3)}
                onCellEditRequest={handleRightCellEdit}
                readOnlyEdit={true}
              />
            </StyledTableWrapper>
          </>
        ) : (
          <>
            <RawProductSelectTable
                ref={availableRawProductGridRef}
                title={'Available Raw Products'}
                onSelected={onSelectionChangedAvailable}
                rowSelection={'multiple'}
                rowMultiSelectWithClick={true}
            />
            <StyledTableWrapper>
              <Table
                ref={selectedGridRef}
                showHeader={true}
                headerTitle={selectedTableHeaderTitle()}
                headerButtons={[
                  {
                    inputProps: {
                      label: '',
                      id: 'addRawProd',
                      testId: 'inputSearchRawProd',
                      errorText: rawProdNoError ? 'Raw product not found' : '',
                      onChange: handleChange,
                      'aria-label': 'add raw product',
                      ref: inputRef,
                      onKeyDown: handleKeyDown,
                    },
                    buttonProps: {
                      label: 'Add Raw Product',
                      testId: 'addRawProductActionButton',
                      onClick: handleAddRawProd,
                    },
                  },
                ]}
                columnDefs={[
                  {
                    minWidth: 70,
                    maxWidth: 70,
                    flex: 1,
                    headerName: 'Select',
                    checkboxSelection: true,
                    floatingFilter: false,
                    sortable: false,
                  },
                  { minWidth: 120, flex: 1, field: 'moaRawProdNo', headerName: 'Raw Product' },
                  { minWidth: 220, flex: 2, field: 'moaRawProdDesc', headerName: 'Description' },
                  {
                    minWidth: 120,
                    flex: 1,
                    field: 'usageAmount',
                    headerName: 'Usage Amount',
                    editable: true,
                    resizable: false,
                    cellRenderer: EditCellRenderer,
                  },
                ]}
                rowData={selectedRawProds}
                rowSelection={flow === 'desiredRawProduct' ? 'single' : 'multiple'}
                suppressRowClickSelection={true}
                onSelectionChanged={onSelectionChangedSelected}
                onCellEditRequest={handleCellEdit}
                readOnlyEdit={true}
                returnedRecords={selectedRawProds.length}
                height={'100%'}
              />
            </StyledTableWrapper>
          </>
        )}
      </TableRowWrapper>
      <Buttons>
        {flow === 'addRawProds' || flow === 'currentRawProduct' ? (
          <div></div>
        ) : (
          <Button label='Back' testId='backActionButton' variant={Variant.Secondary} onClick={() => setStep(0)} />
        )}
        {flow === 'currentRawProduct' ? (
          <Button
            label='Next'
            testId='currentRawProductNextActionButton'
            onClick={handleNextClick}
            disabled={rightRowData.length === 0 || rightRowData.length !== centerRowData.length}
          />
        ) : (
          <Button
            label='Next'
            testId='nextActionButton'
            onClick={handleNextClick}
            disabled={selectedRawProds.length === 0}
          />
        )}
      </Buttons>
    </SelectRawProdsContainer>
  );
};

export default SelectRawProds;
